import React from 'react';
import {
  Popover, Button, Toggle, Checkbox, Box, SpaceBetween, Badge, Header,
} from '@cloudscape-design/components';

/**
 * Per-seller enable/disable for the buyer agent — the portal-discovery allow-list
 * (mirrors the original demo's DiscoveryControl). A popover holds:
 *   - a master "Use discovery portal" switch (usePortal). Off → the agent has NO
 *     seller tools at all (discovery is its ONLY source).
 *   - one checkbox per discovered seller. Unchecking removes it from the allow-list
 *     sent to the agent, so the agent won't discover or pay that seller.
 *
 * State is tracked as a DISABLED set (empty = all enabled, the default). The parent
 * derives `enabledSellers = known − disabled` for the invoke body (omitted when the
 * disabled set is empty → agent uses all).
 */
export default function SellerEnableControl({
  discovery,
  usePortal,
  onUsePortalChange,
  disabledSellers = [],
  onToggleSeller,
  onSetDisabled, // (string[]) => void — set the whole disabled set (Select all / only)
  disabled = false,
}) {
  const sellers = (discovery && discovery.sellers) || [];
  const ids = sellers.map((s) => s.sellerId);
  const disabledSet = new Set(disabledSellers);
  const enabledCount = sellers.filter((s) => !disabledSet.has(s.sellerId)).length;
  const allOn = enabledCount === sellers.length;

  // Bulk helpers (no-ops if the parent didn't wire onSetDisabled).
  const selectAll = () => onSetDisabled && onSetDisabled([]);
  const selectNone = () => onSetDisabled && onSetDisabled(ids.slice());
  const only = (id) => onSetDisabled && onSetDisabled(ids.filter((x) => x !== id));

  const summary = !usePortal
    ? 'off'
    : sellers.length
      ? `${enabledCount}/${sellers.length} sellers`
      : '…';

  const panel = (
    <div style={{ width: 320, maxHeight: '60vh', overflowY: 'auto' }}>
      <SpaceBetween size="s">
        {/* Master switch */}
        <div>
          <Toggle
            checked={!!usePortal}
            onChange={({ detail }) => onUsePortalChange(detail.checked)}
            disabled={disabled}
          >
            Use discovery portal
          </Toggle>
          <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
            {usePortal
              ? 'The agent builds its tools from the connected sellers below.'
              : 'Off — the agent has no seller tools (discovery is its only source).'}
          </Box>
        </div>

        {usePortal && (
          <div>
            <Header
              variant="h3"
              counter={sellers.length ? `(${enabledCount}/${sellers.length})` : undefined}
              actions={
                sellers.length > 0 && onSetDisabled ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="inline-link" disabled={disabled || allOn} onClick={selectAll}>
                      Select all
                    </Button>
                    <Button variant="inline-link" disabled={disabled || enabledCount === 0} onClick={selectNone}>
                      None
                    </Button>
                  </SpaceBetween>
                ) : undefined
              }
            >
              Connected sellers
            </Header>
            {sellers.length === 0 ? (
              <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xs' }}>
                Send a message to discover sellers.
              </Box>
            ) : (
              <SpaceBetween size="xxs">
                {sellers.map((s) => {
                  const off = disabledSet.has(s.sellerId);
                  const skillCount = (s.skills || []).length;
                  return (
                    <div
                      key={s.sellerId}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: '6px 4px', borderBottom: '1px solid #f0f1f3',
                      }}
                    >
                      <Checkbox
                        checked={!off}
                        disabled={disabled}
                        onChange={() => onToggleSeller(s.sellerId)}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                          <Box fontSize="body-s">
                            <span style={{ textDecoration: off ? 'line-through' : 'none', textTransform: 'capitalize', fontWeight: 600 }}>
                              {s.serviceName || s.sellerId}
                            </span>
                          </Box>
                          {/* "only" — enable just this seller, disable the rest. */}
                          {onSetDisabled && sellers.length > 1 && (
                            <Button variant="inline-link" disabled={disabled} onClick={() => only(s.sellerId)}>
                              only
                            </Button>
                          )}
                        </div>
                        <SpaceBetween direction="horizontal" size="xxs">
                          <Badge color="grey">{skillCount} skill{skillCount === 1 ? '' : 's'}</Badge>
                          {s.source ? <Badge>{s.source}</Badge> : null}
                          {off ? <Badge color="red">disabled</Badge> : null}
                        </SpaceBetween>
                      </div>
                    </div>
                  );
                })}
              </SpaceBetween>
            )}
          </div>
        )}
      </SpaceBetween>
    </div>
  );

  return (
    <Popover
      header="Sellers for the agent"
      content={panel}
      triggerType="custom"
      position="bottom"
      size="large"
      dismissButton
    >
      <Button iconName="settings" disabled={disabled}>
        Sellers: {summary}
      </Button>
    </Popover>
  );
}
